/**
 * 🔔 Notification Service
 * 
 * Bildiriş API əməliyyatları üçün servis.
 * 
 * @version 1.0.0
 */

import axios from 'axios';

const API_BASE_URL = 'https://api.herracim.az';

// Axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Error handler
const handleError = (error, defaultMessage) => {
    console.error(defaultMessage, error);
    
    if (error.response) {
        return {
            success: false,
            error: error.response.data?.message || defaultMessage,
            statusCode: error.response.status,
        };
    }
    
    return {
        success: false,
        error: error.message || defaultMessage,
        statusCode: null,
    };
};

// ═══════════════════════════════════════════════════════════════
// 📬 BİLDİRİŞLƏRİ ƏLDƏ ET
// ═══════════════════════════════════════════════════════════════

/**
 * İstifadəçinin bildirişlərini alır
 * @param {Object} options - Filtr parametrləri
 * @returns {Promise<Object>}
 */
export const getNotifications = async (options = {}) => {
    const { page = 1, pageSize = 20, type = null, isRead = null } = options;

    try {
        const params = { page, pageSize };
        if (type) params.type = type;
        if (isRead !== null) params.isRead = isRead;

        const response = await api.get('/api/Notification', { params });
        
        return {
            success: true,
            data: {
                notifications: response.data.notifications || [],
                totalCount: response.data.totalCount || 0,
                unreadCount: response.data.unreadCount || 0,
                currentPage: response.data.currentPage || 1,
                totalPages: response.data.totalPages || 1,
            },
        };
    } catch (error) {
        return handleError(error, 'Bildirişlər alınmadı');
    }
};

/**
 * Oxunmamış bildiriş sayını alır
 * @returns {Promise<Object>}
 */
export const getUnreadCount = async () => {
    try {
        const response = await api.get('/api/Notification/unread-count');
        
        return {
            success: true,
            unreadCount: response.data.unreadCount || 0,
        };
    } catch (error) {
        return { success: false, unreadCount: 0 };
    }
};

// ═══════════════════════════════════════════════════════════════
// ✅ OXUNDU İŞARƏSİ
// ═══════════════════════════════════════════════════════════════

/**
 * Bildirişi oxunmuş işarə edir
 * @param {number} notificationId - Bildiriş ID
 * @returns {Promise<Object>}
 */
export const markAsRead = async (notificationId) => {
    try {
        await api.patch(`/api/Notification/${notificationId}/read`);
        
        return { success: true };
    } catch (error) {
        return handleError(error, 'Bildiriş işarələnə bilmədi');
    }
};

/**
 * Bütün bildirişləri oxunmuş işarə edir
 * @returns {Promise<Object>}
 */
export const markAllAsRead = async () => {
    try {
        const response = await api.post('/api/Notification/mark-all-read');
        
        return {
            success: true,
            count: response.data.count || 0,
        };
    } catch (error) {
        return handleError(error, 'Bildirişlər işarələnə bilmədi');
    }
};

// ═══════════════════════════════════════════════════════════════
// 🗑️ SİLMƏ
// ═══════════════════════════════════════════════════════════════

/**
 * Bildirişi silir
 * @param {number} notificationId - Bildiriş ID
 * @returns {Promise<Object>}
 */
export const deleteNotification = async (notificationId) => {
    try {
        await api.delete(`/api/Notification/${notificationId}`);
        
        return { success: true };
    } catch (error) {
        return handleError(error, 'Bildiriş silinə bilmədi');
    }
};

/**
 * Bütün bildirişləri silir
 * @returns {Promise<Object>}
 */
export const clearAllNotifications = async () => {
    try {
        const response = await api.delete('/api/Notification/clear-all');
        
        return {
            success: true,
            count: response.data.count || 0,
        };
    } catch (error) {
        return handleError(error, 'Bildirişlər silinə bilmədi');
    }
};

// ═══════════════════════════════════════════════════════════════
// 🎨 YARDIMÇI FUNKSİYALAR
// ═══════════════════════════════════════════════════════════════

/**
 * Bildiriş tipinə görə ikon qaytarır
 */
export const getNotificationIcon = (type) => {
    const icons = {
        bid_outbid: '🔔',
        auction_won: '🏆',
        auction_ended: '⏰',
        auction_ended_no_bids: '⏰',
        deposit_refunded: '💰',
        deposit_confirmed: '✅',
        new_bid: '📈',
        report_submitted: '🚨',
        report_approved: '✅',
        report_rejected: 'ℹ️',
        system: 'ℹ️',
        alert: '⚠️',
    };
    return icons[type] || '🔔';
};

/**
 * Bildiriş tipinə görə rəng qaytarır
 */
export const getNotificationColor = (type) => {
    const colors = {
        bid_outbid: 'warning',
        auction_won: 'success',
        auction_ended: 'info',
        auction_ended_no_bids: 'info',
        deposit_refunded: 'success',
        deposit_confirmed: 'success',
        new_bid: 'primary',
        report_submitted: 'warning',
        report_approved: 'success',
        report_rejected: 'info',
        system: 'info',
        alert: 'danger',
    };
    return colors[type] || 'default';
};

/**
 * Bildiriş tipinə görə etiket qaytarır
 */
export const getNotificationLabel = (type) => {
    const labels = {
        bid_outbid: 'Təklif Keçildi',
        auction_won: 'Hərracı Qazandınız',
        auction_ended: 'Hərrac Bitdi',
        auction_ended_no_bids: 'Hərrac Bitdi',
        deposit_refunded: 'Depozit Qaytarıldı',
        deposit_confirmed: 'Ödəniş Alındı',
        new_bid: 'Yeni Təklif',
        report_submitted: 'Şikayət Göndərildi',
        report_approved: 'Şikayət Qəbul Edildi',
        report_rejected: 'Şikayətə Cavab',
        system: 'Sistem',
        alert: 'Xəbərdarlıq',
    };
    return labels[type] || type;
};

/**
 * Tarixi oxunaqlı formatda qaytarır
 */
export const formatNotificationDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'İndicə';
    if (diffMins < 60) return `${diffMins} dəq əvvəl`;
    if (diffHours < 24) return `${diffHours} saat əvvəl`;
    if (diffDays < 7) return `${diffDays} gün əvvəl`;
    
    return date.toLocaleDateString('az-AZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// Default export
const NotificationService = {
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    getNotificationIcon,
    getNotificationColor,
    getNotificationLabel,
    formatNotificationDate,
};

export default NotificationService;